import { makeAutoObservable } from "mobx";

/**
 * One payment attempt. Resolves to `true` when the page is about to navigate away (a payment
 * session was created and the redirect is pending), so the buttons keep spinning, and to
 * `false` when nothing more will happen (e.g. the card-not-accepted dialog opened).
 */
export type PayAttempt = () => Promise<boolean>;

const statusOf = (value: unknown): number | undefined =>
  typeof value === "object" && value !== null && "status" in value && typeof value.status === "number"
    ? value.status
    : undefined;

/**
 * What may be logged about a failed attempt: the HTTP status, or the error's name for a non-HTTP
 * error. Never the error itself, its message or its data: `Api.handleError` rejects with the whole
 * Axios response, whose `config.data` carries the payer's name, email, amount and description.
 */
export function failureSummary(error: unknown): number | string | undefined {
  const response: unknown =
    typeof error === "object" && error !== null && "response" in error ? error.response : undefined;
  const status = statusOf(error) ?? statusOf(response);
  if (status !== undefined) {
    return status;
  }
  return error instanceof Error ? error.name : undefined;
}

/**
 * pp2's own "payment in progress" flag (owner decision Q16). The shared store sets its
 * `inProgress` and never resets it after a failed payment session, so pp2 does not read it:
 * this flag ends with every attempt that does not navigate away, including one that throws,
 * and the buttons can be pressed again.
 */
export class PayProgress {
  private _inProgress = false;

  constructor() {
    makeAutoObservable(this);
  }

  get inProgress(): boolean {
    return this._inProgress;
  }

  private set inProgress(value: boolean) {
    this._inProgress = value;
  }

  /** Runs `attempt` unless one is already running (that press is ignored, as today). */
  async run(attempt: PayAttempt): Promise<void> {
    if (this.inProgress) {
      return;
    }
    this.inProgress = true;
    let navigating: boolean;
    try {
      navigating = await attempt();
    } catch (e) {
      // The store has already set makeTransactionState = "error", which shows the error line
      console.error("pp2: payment attempt failed", failureSummary(e));
      navigating = false;
    }
    if (!navigating) {
      this.inProgress = false;
    }
  }
}
